/**
 * Custom hook for managing Admin Settings state and operations
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { platformSettingsAPI, preferencesAPI, type SiteRegionalSettings, type PreferencesChoices, type PurchasingTabsVisibility, type QualityTabsVisibility } from '../../../lib/api';
import { useMenuSettings, DEFAULT_MENU_VISIBILITY } from '../../../contexts/MenuSettingsContext';
import {
  ThemeSettings,
  PlatformSettings,
  NotificationConfig,
  BarrattFlags,
  DEFAULT_THEME_SETTINGS,
  DEFAULT_BARRATT_FLAGS,
  DEFAULT_DASHBOARD_TABS,
  DEFAULT_CUSTOMER_DASHBOARD_TABS,
  DEFAULT_CUSTOMER_CARD_FIELDS,
  DEFAULT_DATA_IMPORT_TILES,
  DashboardTabsVisibility,
  CustomerCardFieldsVisibility,
  DataImportTilesVisibility,
} from './types';
import { DEFAULT_PURCHASING_TABS, normalizePurchasingTabs, ACTIVE_PURCHASING_TAB_REGISTRY } from './purchasingTabsConfig';

export function useAdminSettings() {
  const { menuVisibility: contextMenuVisibility, updateMenuVisibility, loadSettingsFromBackend, updateDashboardTabs, updateCustomerDashboardTabs, updateCustomerCardFields, updateDataImportTiles } = useMenuSettings();

  // Theme settings state
  const [themeSettings, setThemeSettings] = useState<ThemeSettings>(DEFAULT_THEME_SETTINGS);

  // Platform settings state - Initialize with context menu visibility
  const [platformSettings, setPlatformSettings] = useState<PlatformSettings>(() => ({
    mainMenuType: '2',
    freeMode: false,
    mainCompanyCanLoginAs: false,
    automationRelated: false,
    useDispositionInNCR: false,
    warehouseModule: false,
    turnEmailMAB: false,
    switchPartRepository: false,
    qualityMeasurePPK: false,
    restrictCountry: '',
    restrictCountryEnabled: false,
    menuVisibility: contextMenuVisibility,
    ncrTemplate: 1,
    // Upload Limits (Feature 5)
    maxUploadSizeMb: 50,
    allowedFileTypes: [],
    // Document Versioning (Feature 8)
    autoVersionDocuments: true,
    versionFormat: 'numeric',
    retainOldVersions: true,
    // Notification Defaults (Feature 3)
    defaultNotificationPreferences: {},
    systemAlertEmails: '',
    emailsPaused: false,
    emailRateLimitPerDay: 4,
    notifyJoinedUsersOnly: false,
    // Hierarchy Labels
    hierarchyLevel1Label: 'Group',
    hierarchyLevel2Label: 'Division',
    hierarchyLevel3Label: 'Company',
    customerDefaultLandingPath: '/dashboard',
    poLineSchedules: false,
    jobDocuments: false,
  }));

  // File upload states
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const [saving, setSaving] = useState(false);

  // Company regional settings state
  const [siteSettings, setSiteSettings] = useState<SiteRegionalSettings[]>([]);
  const [choices, setChoices] = useState<PreferencesChoices | null>(null);
  const [loadingSites, setLoadingSites] = useState(false);
  const [savingSite, setSavingSite] = useState<number | null>(null);
  const [editedSites, setEditedSites] = useState<{ [key: number]: Partial<SiteRegionalSettings> }>({});

  // Barratt feature flags state
  const [barrattFlags, setBarrattFlags] = useState<BarrattFlags>(DEFAULT_BARRATT_FLAGS);

  // Dashboard tab visibility state (CR-4441)
  const [dashboardTabs, setDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_DASHBOARD_TABS);
  const [customerDashboardTabs, setCustomerDashboardTabs] = useState<DashboardTabsVisibility>(DEFAULT_CUSTOMER_DASHBOARD_TABS);

  // Purchasing & Quality tab visibility
  const [purchasingTabs, setPurchasingTabs] = useState<PurchasingTabsVisibility>(DEFAULT_PURCHASING_TABS);
  const [qualityTabs, setQualityTabs] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });

  // Customer card field visibility
  const [customerCardFields, setCustomerCardFields] = useState<CustomerCardFieldsVisibility>(DEFAULT_CUSTOMER_CARD_FIELDS);

  // Data Import tile visibility
  const [dataImportTiles, setDataImportTiles] = useState<DataImportTilesVisibility>(DEFAULT_DATA_IMPORT_TILES);

  // Sync platform settings menu visibility with context when context changes
  useEffect(() => {
    setPlatformSettings(prev => ({
      ...prev,
      menuVisibility: contextMenuVisibility,
    }));
  }, [contextMenuVisibility]);

  // Load settings from API
  useEffect(() => {
    loadSettings();
    loadSiteSettings();
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const response = await platformSettingsAPI.getSettings();
      const settings = response.data;

      // Update theme settings
      setThemeSettings({
        companyName: settings.company_name,
        loginTitle: (settings as unknown as Record<string, string>)['login_title'] || '',
        defaultTheme: settings.default_theme,
        availableThemes: settings.available_themes,
        backgroundColor: settings.background_color,
        loginColor: settings.login_color,
        fontColor: settings.font_color,
        buttonColor: settings.button_color,
        website: settings.website,
        supportEmail: settings.support_email,
        salesEmail: settings.sales_email,
        noReplyEmail: settings.no_reply_email,
        contactEmail: (settings as unknown as Record<string, string>)['contact_email'] || '',
        companyAddress: (settings as unknown as Record<string, string>)['company_address'] || '',
        companyPhone: (settings as unknown as Record<string, string>)['company_phone'] || '',
        emailSubject: settings.email_subject,
        emailSignature: settings.email_signature,
      });

      // Update platform settings
      setPlatformSettings({
        mainMenuType: settings.main_menu_type,
        freeMode: settings.free_mode,
        mainCompanyCanLoginAs: settings.main_company_can_login_as,
        automationRelated: settings.automation_related,
        useDispositionInNCR: settings.use_disposition_in_ncr,
        warehouseModule: settings.warehouse_module,
        turnEmailMAB: settings.turn_email_mab,
        switchPartRepository: settings.switch_part_repository,
        qualityMeasurePPK: settings.quality_measure_ppk,
        restrictCountry: settings.restrict_country,
        restrictCountryEnabled: settings.restrict_country_enabled,
        // Merge defaults so admin toggles reflect the intended default state
        // even when the backend's stored dict is missing newer keys
        // (e.g. partsMaster). Without this merge, a missing key renders the
        // Switch as unchecked and any save will omit that key, so the
        // sidebar silently falls back to its own default and stays visible.
        menuVisibility: { ...DEFAULT_MENU_VISIBILITY, ...settings.menu_visibility },
        ncrTemplate: ((settings as unknown as Record<string, number>)['ncr_template'] || 1) as 1 | 2,
        // Upload Limits (Feature 5)
        maxUploadSizeMb: settings.max_upload_size_mb ?? 50,
        allowedFileTypes: settings.allowed_file_types ?? [],
        // Document Versioning (Feature 8)
        autoVersionDocuments: settings.auto_version_documents ?? true,
        versionFormat: settings.version_format ?? 'numeric',
        retainOldVersions: settings.retain_old_versions ?? true,
        // Notification Defaults (Feature 3)
        defaultNotificationPreferences: (settings.default_notification_preferences ?? {}) as NotificationConfig,
        systemAlertEmails: settings.system_alert_emails ?? '',
        emailsPaused: settings.emails_paused ?? false,
        emailRateLimitPerDay: settings.email_rate_limit_per_day ?? 4,
        notifyJoinedUsersOnly: settings.notify_joined_users_only ?? false,
        // Hierarchy Labels
        hierarchyLevel1Label: settings.hierarchy_level_1_label ?? 'Group',
        hierarchyLevel2Label: settings.hierarchy_level_2_label ?? 'Division',
        hierarchyLevel3Label: settings.hierarchy_level_3_label ?? 'Company',
        customerDefaultLandingPath: settings.customer_default_landing_path ?? '/dashboard',
        poLineSchedules: settings.feature_flags?.po_line_schedules ?? false,
        jobDocuments: settings.feature_flags?.job_documents ?? false,
      });

      // Also update context with loaded menu visibility (defaults merged)
      updateMenuVisibility({ ...DEFAULT_MENU_VISIBILITY, ...settings.menu_visibility });

      // Load dashboard tab visibility (CR-4441)
      if (settings.dashboard_tabs) {
        setDashboardTabs({
          ...DEFAULT_DASHBOARD_TABS,
          ...settings.dashboard_tabs,
        });
      }
      if (settings.customer_dashboard_tabs) {
        setCustomerDashboardTabs({
          ...DEFAULT_CUSTOMER_DASHBOARD_TABS,
          ...settings.customer_dashboard_tabs,
        });
      }
      // Load purchasing & quality tab visibility
      if (settings.purchasing_tabs) {
        setPurchasingTabs(normalizePurchasingTabs(settings.purchasing_tabs));
      }
      if (settings.quality_tabs) {
        setQualityTabs((prev: QualityTabsVisibility) => ({ ...prev, ...settings.quality_tabs }));
      }
      // Load customer card field visibility
      if (settings.customer_card_fields) {
        setCustomerCardFields({
          ...DEFAULT_CUSTOMER_CARD_FIELDS,
          ...settings.customer_card_fields,
        });
      }
      // Load Data Import tile visibility
      if (settings.data_import_tiles) {
        setDataImportTiles({
          ...DEFAULT_DATA_IMPORT_TILES,
          ...settings.data_import_tiles,
        });
      }

      // Load Barratt feature flags
      if (settings.feature_flags) {
        setBarrattFlags({
          delivery_appeals: settings.feature_flags.delivery_appeals ?? false,
          spend_leakage: settings.feature_flags.spend_leakage ?? false,
          bulk_upload: settings.feature_flags.bulk_upload ?? false,
          commodity_codes: settings.feature_flags.commodity_codes ?? false,
          allow_customer_decline: settings.feature_flags.allow_customer_decline ?? false,
          show_forecast_total: settings.feature_flags.show_forecast_total ?? false,
          portal_mode: settings.feature_flags.portal_mode ?? 'standard',
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    }
  }, [updateMenuVisibility]);

  const loadSiteSettings = useCallback(async () => {
    setLoadingSites(true);
    try {
      const [sitesRes, choicesRes] = await Promise.all([
        preferencesAPI.getSiteRegionalSettings(),
        preferencesAPI.getPreferencesChoices(),
      ]);
      setSiteSettings(Array.isArray(sitesRes.data) ? sitesRes.data : []);
      setChoices(choicesRes.data);
    } catch (error) {
      console.error('Failed to load site settings:', error);
      toast.error('Failed to load company regional settings');
    } finally {
      setLoadingSites(false);
    }
  }, []);

  const handleSiteSettingChange = useCallback((siteId: number, field: string, value: string | null) => {
    setEditedSites(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        [field]: value === '' ? null : value,
      },
    }));
  }, []);

  const saveSiteSettings = useCallback(async (siteId: number) => {
    const edits = editedSites[siteId];
    if (!edits) return;

    setSavingSite(siteId);
    try {
      await preferencesAPI.updateSiteRegionalSettings(siteId, edits);
      toast.success('Company regional settings saved successfully!');
      // Clear edits for this site
      setEditedSites(prev => {
        const { [siteId]: _, ...rest } = prev;
        return rest;
      });
      // Reload site settings
      loadSiteSettings();
    } catch (error) {
      console.error('Failed to save site settings:', error);
      toast.error('Failed to save company regional settings');
    } finally {
      setSavingSite(null);
    }
  }, [editedSites, loadSiteSettings]);

  const saveThemeSettings = useCallback(async () => {
    // Platform Name is required by the backend model (no blank=True).
    // Trim+validate here so blanking Sign-in Page Heading alone still
    // saves cleanly instead of the whole PATCH being rejected with a
    // "company_name: This field may not be blank" 400.
    if (!themeSettings.companyName.trim()) {
      toast.error('Platform Name is required', {
        description: 'Enter a value in Main Company Details before saving.',
        duration: 4000,
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        company_name: themeSettings.companyName.trim(),
        login_title: themeSettings.loginTitle,
        default_theme: themeSettings.defaultTheme,
        available_themes: themeSettings.availableThemes,
        background_color: themeSettings.backgroundColor,
        login_color: themeSettings.loginColor,
        font_color: themeSettings.fontColor,
        button_color: themeSettings.buttonColor,
        website: themeSettings.website,
        support_email: themeSettings.supportEmail,
        sales_email: themeSettings.salesEmail,
        no_reply_email: themeSettings.noReplyEmail,
        contact_email: themeSettings.contactEmail,
        company_address: themeSettings.companyAddress,
        company_phone: themeSettings.companyPhone,
        email_subject: themeSettings.emailSubject,
        email_signature: themeSettings.emailSignature,
      };

      await platformSettingsAPI.patchSettings(payload);

      toast.success('Theme settings saved successfully!', {
        description: 'Your branding preferences have been updated.',
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save theme settings', {
        description: 'Please try again or contact support.',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  }, [themeSettings]);

  const savePlatformSettings = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        main_menu_type: platformSettings.mainMenuType,
        free_mode: platformSettings.freeMode,
        main_company_can_login_as: platformSettings.mainCompanyCanLoginAs,
        automation_related: platformSettings.automationRelated,
        use_disposition_in_ncr: platformSettings.useDispositionInNCR,
        warehouse_module: platformSettings.warehouseModule,
        turn_email_mab: platformSettings.turnEmailMAB,
        switch_part_repository: platformSettings.switchPartRepository,
        quality_measure_ppk: platformSettings.qualityMeasurePPK,
        restrict_country: platformSettings.restrictCountry,
        restrict_country_enabled: platformSettings.restrictCountryEnabled,
        menu_visibility: platformSettings.menuVisibility,
        ncr_template: platformSettings.ncrTemplate,
        feature_flags: {
          ...barrattFlags,
          po_line_schedules: platformSettings.poLineSchedules,
          job_documents: platformSettings.jobDocuments,
        },
        // Upload Limits (Feature 5)
        max_upload_size_mb: platformSettings.maxUploadSizeMb,
        allowed_file_types: platformSettings.allowedFileTypes,
        // Document Versioning (Feature 8)
        auto_version_documents: platformSettings.autoVersionDocuments,
        version_format: platformSettings.versionFormat,
        retain_old_versions: platformSettings.retainOldVersions,
        // Notification Defaults (Feature 3)
        default_notification_preferences: platformSettings.defaultNotificationPreferences,
        system_alert_emails: platformSettings.systemAlertEmails,
        emails_paused: platformSettings.emailsPaused,
        email_rate_limit_per_day: platformSettings.emailRateLimitPerDay,
        notify_joined_users_only: platformSettings.notifyJoinedUsersOnly,
        // Dashboard Tab Visibility (CR-4441)
        dashboard_tabs: dashboardTabs,
        // Hierarchy Labels
        hierarchy_level_1_label: platformSettings.hierarchyLevel1Label,
        hierarchy_level_2_label: platformSettings.hierarchyLevel2Label,
        hierarchy_level_3_label: platformSettings.hierarchyLevel3Label,
        customer_default_landing_path: platformSettings.customerDefaultLandingPath,
      };

      await platformSettingsAPI.patchSettings(payload);

      // Also update context with new menu visibility
      updateMenuVisibility(platformSettings.menuVisibility);

      // Refresh all settings from backend
      await loadSettingsFromBackend();

      // Count enabled menu items
      const enabledCount = Object.values(platformSettings.menuVisibility).filter(v => v).length;

      toast.success('Platform settings saved successfully!', {
        description: `Menu visibility updated. ${enabledCount} menu items are now visible.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save platform settings', {
        description: 'Please try again or contact support.',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  }, [platformSettings, barrattFlags, dashboardTabs, updateMenuVisibility, loadSettingsFromBackend]);

  // CR-4441: Dedicated save for dashboard tab visibility
  const saveDashboardTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({
        dashboard_tabs: dashboardTabs,
        customer_dashboard_tabs: customerDashboardTabs,
      });

      updateDashboardTabs(dashboardTabs);
      updateCustomerDashboardTabs(customerDashboardTabs);

      toast.success('Dashboard settings saved successfully!');
    } catch (error) {
      console.error('Failed to save dashboard settings:', error);
      toast.error('Failed to save dashboard settings');
    } finally {
      setSaving(false);
    }
  }, [dashboardTabs, customerDashboardTabs, updateDashboardTabs, updateCustomerDashboardTabs]);

  const savePurchasingTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ purchasing_tabs: normalizePurchasingTabs(purchasingTabs) });
      const enabledCount = ACTIVE_PURCHASING_TAB_REGISTRY.filter(
        (tab) => normalizePurchasingTabs(purchasingTabs)[tab.id],
      ).length;
      toast.success('Sales Orders settings saved!', {
        description: `${enabledCount} sales orders tabs enabled.`,
      });
    } catch { toast.error('Failed to save sales orders settings'); }
    finally { setSaving(false); }
  }, [purchasingTabs]);

  const saveQualityTabs = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ quality_tabs: qualityTabs });
      const enabledCount = Object.values(qualityTabs).filter(v => v).length;
      toast.success('Quality settings saved!', {
        description: `${enabledCount} quality tabs enabled.`,
      });
    } catch { toast.error('Failed to save quality settings'); }
    finally { setSaving(false); }
  }, [qualityTabs]);

  const saveCustomerCardFields = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ customer_card_fields: customerCardFields });
      updateCustomerCardFields(customerCardFields);
      const enabledCount = Object.values(customerCardFields).filter(v => v).length;
      toast.success('Supplier card settings saved!', {
        description: `${enabledCount} card elements enabled.`,
      });
    } catch { toast.error('Failed to save supplier card settings'); }
    finally { setSaving(false); }
  }, [customerCardFields, updateCustomerCardFields]);

  const saveDataImportTiles = useCallback(async () => {
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({ data_import_tiles: dataImportTiles });
      updateDataImportTiles(dataImportTiles);
      const enabledCount = Object.values(dataImportTiles).filter(v => v).length;
      toast.success('Data Import tiles saved!', {
        description: `${enabledCount} tiles enabled.`,
      });
    } catch { toast.error('Failed to save Data Import tiles'); }
    finally { setSaving(false); }
  }, [dataImportTiles, updateDataImportTiles]);

  const handleFileUpload = useCallback((fileType: string) => {
    // Map fileType keys to accepted file types
    const acceptMap: Record<string, string> = {
      companyLogo: 'image/png,image/jpeg,image/svg+xml',
      companyDarkLogo: 'image/png,image/jpeg,image/svg+xml',
      headerLogo: 'image/png,image/jpeg,image/svg+xml',
      loginImage: 'image/png,image/jpeg,image/webp',
      termsConditions: 'application/pdf',
      privacyPolicy: 'application/pdf',
    };

    // Map fileType keys to backend field names
    const fieldMap: Record<string, string> = {
      companyLogo: 'company_logo',
      companyDarkLogo: 'company_dark_logo',
      headerLogo: 'header_logo',
      loginImage: 'login_image',
      termsConditions: 'terms_conditions',
      privacyPolicy: 'privacy_policy',
    };

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptMap[fileType] || 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      setUploadingFiles(prev => ({ ...prev, [fileType]: true }));
      try {
        const formData = new FormData();
        formData.append(fieldMap[fileType] || fileType, file);

        // Use the patch endpoint with FormData for file upload
        await platformSettingsAPI.patchSettings(formData as unknown as Record<string, unknown>);
        toast.success(`${fileType} uploaded successfully!`);
        loadSettings();
      } catch (error) {
        console.error(`Failed to upload ${fileType}:`, error);
        toast.error(`Failed to upload ${fileType}`);
      } finally {
        setUploadingFiles(prev => ({ ...prev, [fileType]: false }));
      }
    };
    input.click();
  }, [loadSettings]);

  const resetColorsToDefault = useCallback(async () => {
    const defaultColors = {
      backgroundColor: '#ffffff',
      loginColor: '#4f46e5',
      fontColor: '#333333',
      buttonColor: '#4f46e5',
    };
    setThemeSettings(prev => ({
      ...prev,
      ...defaultColors,
    }));
    setSaving(true);
    try {
      await platformSettingsAPI.patchSettings({
        background_color: defaultColors.backgroundColor,
        login_color: defaultColors.loginColor,
        font_color: defaultColors.fontColor,
        button_color: defaultColors.buttonColor,
      });
      toast.success('Colors reset to defaults!');
    } catch (error) {
      console.error('Failed to reset colors:', error);
      toast.error('Failed to reset colors');
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    // Theme state
    themeSettings,
    setThemeSettings,

    // Platform state
    platformSettings,
    setPlatformSettings,

    // Barratt flags
    barrattFlags,
    setBarrattFlags,

    // Dashboard tab visibility (CR-4441)
    dashboardTabs,
    setDashboardTabs,
    customerDashboardTabs,
    setCustomerDashboardTabs,
    purchasingTabs,
    setPurchasingTabs,
    savePurchasingTabs,
    qualityTabs,
    setQualityTabs,
    saveQualityTabs,
    customerCardFields,
    setCustomerCardFields,
    saveCustomerCardFields,
    dataImportTiles,
    setDataImportTiles,
    saveDataImportTiles,

    // Site settings state
    siteSettings,
    choices,
    loadingSites,
    savingSite,
    editedSites,

    // File upload
    uploadingFiles,
    saving,

    // Actions
    saveThemeSettings,
    savePlatformSettings,
    saveDashboardTabs,
    handleFileUpload,
    resetColorsToDefault,
    handleSiteSettingChange,
    saveSiteSettings,
    loadSiteSettings,
  };
}
